import { useMemo, useState } from "react";
import { cloneDeep } from "lodash";

import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MedicalNotesConfigurationHelper } from "@/Patient/Helpers/MedicalNotesConfigurationHelper";
import { NotesConfigurationModel } from "@/Patient/Models/NotesConfigurationModel";

import styles from "./MedicalNotesConfiguration.module.scss";

interface IMedicalNotesConfigurationPopupProps {
    onCancel: () => void;
    onApplyToAllPatients: (configs: NotesConfigurationModel, dateFrom?: Date, dateTo?: Date) => Promise<void>;
    onApplyToCurrentPatient: (configs: NotesConfigurationModel, patientId: string, dateFrom?: Date, dateTo?: Date) => Promise<void>;
}

function MedicalNotesConfigurationPopup(props: IMedicalNotesConfigurationPopupProps): JSX.Element {
    const { getText } = useTranslation();
    const [dateFrom, setDateFrom] = useState<Date>();
    const [dateTo, setDateTo] = useState<Date>();

    const consultationContexts = cacheStore?.FindLookupsByTypeCode("CONSULTATIONCONTEXT").sortByProperty(c => c.GetValue());
    const specificOrDefaultConfigurations = MedicalNotesConfigurationHelper.getSpecificConfigAndUpdateNotesFilterSettings();
    const [medicalNotes, setMedicalNotes] = useState<NotesConfigurationModel>(specificOrDefaultConfigurations);

    const consultationLookupIds = useMemo(() => consultationContexts.map(consultationContexts => consultationContexts.LookupID), [consultationContexts]);
    const isAllChecked = useMemo(() => consultationLookupIds.every(lookupId => medicalNotes.EncounterContexts.includes(lookupId)), [consultationLookupIds, medicalNotes]);

    function renderFooters(): JSX.Element {
        return (
            <div className="p-footer">
                <div className="space-between">
                    <div className="flex-container flex-end">
                        <button className="btn btn-default margin-sm-left" onClick={() => props.onCancel()}>
                            {getText("CancelLabel")}
                        </button>
                        <button className="btn btn-primary margin-sm-left" onClick={() => props.onApplyToAllPatients(medicalNotes, dateFrom, dateTo)}>
                            {getText("MedicalSummary_ApplyToAllPatientsLabel")}
                        </button>
                        <button className="btn btn-primary margin-sm-left" onClick={() => props.onApplyToCurrentPatient(medicalNotes, patientStore.getCurrentPatientContactId(), dateFrom, dateTo)} data-cy="medicalNotesConfiguration-apply">
                            {getText("MedicalSummary_ApplyToThisPatientLabel")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    function changeSelectAllState(isChecked: boolean): void {
        setMedicalNotes(currentMedicalNotes => {
            const notes = cloneDeep(currentMedicalNotes);
            notes.EncounterContexts = isChecked ? consultationLookupIds : [];
            return notes;
        });
    }

    function capitalizeFirstLetter(text: string): string {
        return text[0].toUpperCase() + text.toLowerCase().slice(1);
    }

    function consultationContextCheckboxChange(isChecked: boolean, contextId: string): void {
        setMedicalNotes(currentMedicalNotes => {
            const notes = cloneDeep(currentMedicalNotes);
            if (isChecked) {
                notes.EncounterContexts.push(contextId);
            }
            else {
                const index = notes.EncounterContexts.indexOf(contextId);
                notes.EncounterContexts.splice(index, 1);
            }
            return notes;
        });
    }

    const changeDisplayVisitDiagnosisState = (isChecked: boolean): void => {
        setMedicalNotes(currentMedicalNotes => {
            const notes = cloneDeep(currentMedicalNotes);
            notes.DisplayVisitDiagnosis = isChecked;

            return notes;
        });
    };

    return (
        <Modal
            centerContent
            draggable
            title={getText("ConfigureNotes_TitleLabel")}
            width={510}
            showOverlay
            disableOverlayClose
            showX
            onClose={() => props.onCancel()}
            footer={renderFooters()}
            dataCy="medicalNotesConfiguration"
        >
            <div className="col-lg-12 margin-sm-bottom">
                <div className={styles.multiDateSelectorContainer}>
                    <label style={{ fontWeight: "bold" }}>{getText("ConfigureNotes_DateRangeLabel")}</label>
                    <MultiDateSelector
                        id="notes_configuration_date_selector"
                        dataCy="medicalNote-search-dates"
                        fromSetTodayLabel={getText("TodayLabel")}
                        toSetTodayLabel={getText("TodayLabel")}
                        from={dateFrom}
                        to={dateTo}
                        cssClass={styles.notesConfiguration}
                        fromLabel={getText("FromLabel")}
                        toLabel={getText("ToLabel")}
                        fromChange={date => setDateFrom(date)}
                        toChange={date => setDateTo(date)}
                        fieldContainerCssClass={styles.multiDateSelectorField}
                    />
                </div>
                <div>
                    {cacheStore.ClinicSettings.EnableDiagnosisOnPatientNoteTile && (
                        <CheckboxControl
                            id="notes_configuration_note_display_visit_diagnosis_checkbox"
                            label={getText("DisplayDiagnosisInNote_DisplayVisitDiagnosis")}
                            labelStyle={{ fontWeight: "bold" }}
                            checked={medicalNotes.DisplayVisitDiagnosis}
                            onChange={isChecked => changeDisplayVisitDiagnosisState(isChecked)}
                        />
                    )}
                </div>
                <div>
                    <CheckboxControl
                        id="notes_configuration_note_context_checkbox"
                        dataCy="medicalNotesConfiguration-context"
                        label={getText("ContextLabel")}
                        labelStyle={{ fontWeight: "bold" }}
                        checked={isAllChecked}
                        onChange={isChecked => changeSelectAllState(isChecked)}
                    />
                </div>

                <div className={styles.consultationContextContainer}>
                    {consultationContexts?.map(consultationContext => (
                        <div key={consultationContext.LookupID}>
                            <CheckboxControl
                                id={consultationContext.LookupID}
                                label={capitalizeFirstLetter(getText(consultationContext.GetValue()))}
                                checked={medicalNotes.EncounterContexts.includes(consultationContext.LookupID)}
                                onChange={isChecked => consultationContextCheckboxChange(isChecked, consultationContext.LookupID)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    );
}

export { MedicalNotesConfigurationPopup };
